import React, { useEffect, useMemo, useState } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import usePermission from '@/hooks/usePermission';
import { confirm } from '@/lib/confirm';
import promiseRequest from '@/utils/promiseToast';
import { TiaBanner } from '@/components/custom/tia-banner';
import { DashboardContent } from '@/layouts/dashboard';
import { Filter, FilterContent, FilterActions, FilterSelectNative } from '@/components/custom/filter';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import { getLiveClassStatus } from '../PlayfulShared/live-class-status';
import { LiveClassesCalendar } from '../PlayfulShared/LiveClassesCalendar';
import { LiveClassCardTeacher } from './LiveClassCardTeacher';
import { LiveClassDetailModal } from './LiveClassDetailModal';
import { LiveClassFormModal } from './LiveClassFormModal';

export default function LiveClassesPlayfulTeacher({ uri }) {
  const client = useClient();
  const { promiseToast, isSubmitting } = promiseRequest();

  const [relationships, setRelationships] = useState({ courses: [] });
  const [weekDays, setWeekDays] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('blank');
  const [selectedLiveClass, setSelectedLiveClass] = useState(null);
  const [formState, setFormState] = useState(null); // null | { mode: 'create' } | { mode: 'edit', liveClass }

  const canCreate = usePermission('Criar aulas ao vivo', 'Aulas ao vivo');

  useEffect(() => {
    const getRelationships = async () => {
      const { data: courses } = await client.fetch('courses/simple');
      setRelationships({ courses: courses || [] });
    };

    getRelationships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const courseOptions = useMemo(() => relationships.courses.map(course => ({ id: course.id, name: course.title })), [relationships.courses]);

  const filteredLiveClasses = selectedCourse === 'blank' ? weekDays : weekDays.filter(liveClass => liveClass.courses?.some(course => course.id === Number(selectedCourse)));

  const closeForm = () => setFormState(null);

  const handleSaveForm = values => {
    if (formState?.mode === 'edit') {
      const editingId = formState.liveClass.id;

      promiseToast({
        url: `live_classrooms/${editingId}`,
        request: values,
        successText: 'Aula atualizada com sucesso!',
        errorText: 'Não foi possível atualizar a aula.'
      }).then(data => {
        setWeekDays(prev => prev.map(item => (item.id === data.id ? data : item)));
        closeForm();
      });
    } else {
      promiseToast({
        url: 'live_classrooms',
        request: values,
        successText: 'Aula criada com sucesso!',
        errorText: 'Não foi possível criar a aula.'
      }).then(data => {
        setWeekDays(prev => [...prev, data].sort((a, b) => new Date(a['initial-date']) - new Date(b['initial-date'])));
        closeForm();
      });
    }
  };

  const handleDeleteLiveClass = async liveClass => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir esta aula?',
      description: 'Essa ação não pode ser desfeita.',
      confirmText: 'Sim',
      cancelText: 'Não'
    });

    if (!confirmed) return;

    const { error } = await client.delete(`live_classrooms/${liveClass.id}`);

    if (error) {
      toast.error('Não foi possível excluir a aula.');
    } else {
      setWeekDays(prev => prev.filter(item => item.id !== liveClass.id));
      toast.success('Aula excluída com sucesso!');
    }
  };

  const breadCrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: uri,
        name: 'Aulas ao vivo',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadCrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-20"
        image={JOINHA}
        imageAlt="Personagem do professor"
        imageHeight={210}
        imageOffset={200}
        bubbleCorner="tl"
        bubbleMaxWidth={420}
        text="Suas aulas ao vivo estão aqui! Acompanhe o que já rolou, o que tá rolando agora e o que vem por aí. 🎥"
      />

      <Filter variant="plain">
        <FilterContent>
          <FilterSelectNative
            label="Filtrar por curso"
            value={selectedCourse}
            onChange={e => setSelectedCourse(e.target.value)}
            blankLabel="Todos os cursos"
            options={courseOptions}
          />
        </FilterContent>

        {canCreate && (
          <FilterActions>
            <PlayfulCardButton
              className="w-auto px-6"
              onClick={() => setFormState({ mode: 'create' })}
            >
              Criar aula
            </PlayfulCardButton>
          </FilterActions>
        )}
      </Filter>

      <LiveClassesCalendar
        liveClasses={filteredLiveClasses}
        setLiveClasses={setWeekDays}
        renderCard={(liveClass, status) => (
          <LiveClassCardTeacher
            liveClass={liveClass}
            status={status}
            onClick={() => setSelectedLiveClass(liveClass)}
            onEdit={item => setFormState({ mode: 'edit', liveClass: item })}
            onDelete={handleDeleteLiveClass}
          />
        )}
      />

      <LiveClassDetailModal
        liveClass={selectedLiveClass}
        status={selectedLiveClass ? getLiveClassStatus(selectedLiveClass) : null}
        onClose={() => setSelectedLiveClass(null)}
      />

      <LiveClassFormModal
        show={Boolean(formState)}
        mode={formState?.mode}
        liveClass={formState?.mode === 'edit' ? formState.liveClass : null}
        isSubmitting={isSubmitting}
        onClose={closeForm}
        onSubmit={handleSaveForm}
      />
    </DashboardContent>
  );
}
